import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, FileTextIcon, MessageCircleIcon, ShieldCheckIcon } from 'lucide-react';
import { FactIcon } from './FactIcon';
import { getTreatmentFacts } from '../../utils/treatmentFacts';
import type { Treatment } from '../../types/care';
interface TreatmentHeroProps {
  treatment: Treatment;
}
export function TreatmentHero({
  treatment
}: TreatmentHeroProps) {
  const facts = getTreatmentFacts(treatment.facts);
  return <section className="bg-white" data-id="element-327" data-mp="1v841rxw4y7mj">
      <div className="bg-ink text-white" data-id="element-328" data-mp="1v72fex9o5ypi">
        <div className="mx-auto max-w-7xl px-5 pb-28 pt-8 lg:px-8 lg:pb-32 lg:pt-10" data-id="element-329" data-mp="1va6pucdq9qom">
          <nav aria-label="Kruimelpad" data-id="element-330" data-mp="1heuvi63hi288">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-white/60" data-id="element-331" data-mp="1mfijsk0bw4yr">
              <li data-id="element-332" data-mp="2a5weyje7mjxk">
                <Link to="/" className="hover:text-white" data-id="element-333" data-mp="2a5cvgy8wvnhf">
                  Home
                </Link>
              </li>
              <li className="flex items-center gap-1.5" data-id="element-334" data-mp="2exwb00r5ssov">
                <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-335" data-mp="w4ztnmxyn6ed" />
                <Link to="/behandelingen" className="hover:text-white" data-id="element-336" data-mp="w2wzqrtost4d">
                  Behandelingen
                </Link>
              </li>
              <li className="flex items-center gap-1.5" data-id="element-337" data-mp="15w490nrjst9q">
                <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-338" data-mp="114cjmgk4otl6" />
                <span aria-current="page" className="text-white" data-id="element-339" data-mp="1129jh3p9hk2q">
                  {treatment.name}
                </span>
              </li>
            </ol>
          </nav>

          <p className="mt-10 font-display text-base font-medium text-cyan" data-id="element-340" data-mp="1atdz85mrh8ym">{treatment.category}</p>
          <h1 className="mt-3 max-w-4xl font-display text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-id="element-341" data-mp="1otewzc7qc09v">
            {treatment.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/75" data-id="element-342" data-mp="3qkibickqpyzp">{treatment.summary}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-343" data-mp="3ln062w5xm1wg">
            {treatment.pdf && <a href={treatment.pdf} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-ink transition-colors duration-150 hover:bg-primary-soft" data-id="element-344" data-mp="1pk3wznxprj50">
                <FileTextIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-345" data-mp="23gqvhv5behd1" />
                Download patiëntenfolder
              </a>}
            <Link to="/contact" className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3.5 text-[15px] font-semibold text-white ring-1 ring-white/30 transition-colors duration-150 hover:bg-white/10" data-id="element-346" data-mp="2dfzke9lbp5sj">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-347" data-mp="2r8pk1gtry984" />
              Contact
            </Link>
          </div>
        </div>
      </div>

      <div className="relative mx-auto -mt-16 max-w-7xl px-5 lg:px-8" data-id="element-348" data-mp="2w7sek7c4ox1u">
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl bg-line shadow-[0_20px_50px_-30px_rgba(3,59,74,0.45)] ring-1 ring-line md:grid-cols-3 lg:grid-flow-col lg:auto-cols-fr lg:grid-cols-none" data-id="element-349" data-mp="kl8ca7to4ily">
          {facts.map(fact => <div key={fact.kind} className="flex items-start gap-3 bg-white p-5" data-id="element-350" data-mp="blbtunxvgfzc">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary" data-id="element-351" data-mp="6mh38ztnxn4o">
                <FactIcon kind={fact.kind} data-id="element-352" data-mp="z91rvsir7l4j" />
              </span>
              <div className="min-w-0" data-id="element-353" data-mp="z9v3b6snqyt0">
                <dt className="text-xs font-medium text-muted" data-id="element-354" data-mp="ui3diyhbsx3x">{fact.label}</dt>
                <dd className="mt-0.5 text-[15px] font-medium leading-snug text-ink" data-id="element-355" data-mp="ug0c7iaq3hjw">{fact.value}</dd>
              </div>
            </div>)}
          <div className="flex items-start gap-3 bg-white p-5" data-id="element-356" data-mp="1mzpdfglle99k">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary" data-id="element-357" data-mp="1i87f70ayemsa">
              <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-358" data-mp="11s56ypww631h" />
            </span>
            <div className="min-w-0" data-id="element-359" data-mp="16s1548iwr094">
              <dt className="text-xs font-medium text-muted" data-id="element-360" data-mp="16t2n6wyccn0c">Vergoeding</dt>
              <dd className="mt-0.5 text-[15px] font-medium leading-snug text-ink" data-id="element-361" data-mp="ssrxvycojgha">Basisverzekering</dd>
            </div>
          </div>
        </dl>
      </div>
    </section>;
}